import { Tooltip, DrillDownPie } from '@clickcease/cc-client-components-lib'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ThreatGroupToolTip from '../../../../../features/Dashboard/components/FederatedContentComponents/ThreatTypesAndGroupsWidget/components/ThreatGroupTooltip'
import { InsightsEntry, NormalizedSummaryData } from '../../../../../hooks/api/BMDataTypes'

import {
  ChartDataPoint,
  ChartOptions,
  DrilldownSeries,
  PieColors,
  SeriesPieOptions,
} from './ThreatTypesAndGroupsWidget.models'
import {
  ThreatTypesAndGroupsWidgetWrapper,
  ThreatTypesAndGroupsWidgetTitle,
  ThreatTypesAndGroupsWidgetCard,
  ThreatTypesAndGroupsWidgetTitleText,
} from './ThreatTypesAndGroupsWidget.styled'

interface ThreatTypesAndGroupsWidgetProps {
  isPdf?: boolean
  summary: NormalizedSummaryData
}

export const ThreatTypesAndGroupsWidget: React.FC<ThreatTypesAndGroupsWidgetProps> = ({
  isPdf,
  summary,
}) => {
  const { t } = useTranslation('botMitigation')

  const insights = useMemo<InsightsEntry[]>(() => {
    return summary.rawData?.insights ?? []
  }, [summary.rawData])

  const pieChartData = useMemo(() => {
    const threatGroupTotals: Record<string, number> = {}
    const botActivityBreakdown: ChartDataPoint[] = []
    const maliciousActivityBreakdown: ChartDataPoint[] = []

    insights.forEach((entry) => {
      if (!threatGroupTotals[entry.threatGroup]) {
        threatGroupTotals[entry.threatGroup] = 0
      }
      threatGroupTotals[entry.threatGroup] += entry.totalBlocked
      if (entry.threatGroup === t('threatGroups.tooltips.invalidBotActivity.title')) {
        botActivityBreakdown.push({
          name: entry.threatType,
          y: entry.totalBlocked,
          invalidRate: entry.invalidRate,
        })
      } else if (entry.threatGroup === t('threatGroups.tooltips.invalidMalicious.title')) {
        maliciousActivityBreakdown.push({
          name: entry.threatType,
          y: entry.totalBlocked,
          invalidRate: entry.invalidRate,
        })
      }
    })

    const threatGroupData: ChartDataPoint[] = Object.entries(threatGroupTotals).map(
      ([name, value]) => ({
        name,
        y: value,
        drilldown: name,
      })
    )

    const drilldownSeries: DrilldownSeries[] = [
      {
        id: t('threatGroups.tooltips.invalidBotActivity.title'),
        name: t('threatGroups.tooltips.invalidBotActivity.title'),
        type: 'pie',
        data: botActivityBreakdown,
      },
      {
        id: t('threatGroups.tooltips.invalidMalicious.title'),
        name: t('threatGroups.tooltips.invalidMalicious.title'),
        type: 'pie',
        data: maliciousActivityBreakdown,
      },
    ]

    return {
      threatGroupData,
      drilldownSeries,
    }
  }, [insights, t])

  const dataCast = (data: ChartDataPoint[]): SeriesPieOptions['data'] => data
  const drilldownCast = (series: DrilldownSeries[]): DrilldownSeries[] => series

  return (
    <ThreatTypesAndGroupsWidgetWrapper>
      <ThreatTypesAndGroupsWidgetCard>
        <ThreatTypesAndGroupsWidgetTitle>
          <ThreatTypesAndGroupsWidgetTitleText>
            {t('threatGroups.title')}
          </ThreatTypesAndGroupsWidgetTitleText>
          <ThreatGroupToolTip
            onOpenChange={() => {}}
            source="main"
            trigger={<Tooltip tooltiptContent={t('threatGroups.tooltips.hover')} side="left" />}
          />
        </ThreatTypesAndGroupsWidgetTitle>
        <DrillDownPie
          data={dataCast(pieChartData.threatGroupData)}
          drillDownSeries={drilldownCast(pieChartData.drilldownSeries)}
          pieColors={PieColors}
          pieSize={isPdf ? 60 : 100}
          options={ChartOptions}
        />
      </ThreatTypesAndGroupsWidgetCard>
      <ThreatTypesAndGroupsWidgetCard>
        <ThreatTypesAndGroupsWidgetTitle>
          <ThreatTypesAndGroupsWidgetTitleText>
            {t('threatGroups.invalidBotActivity.title')}
          </ThreatTypesAndGroupsWidgetTitleText>
          <ThreatGroupToolTip
            onOpenChange={() => {}}
            source="botActivity"
            trigger={<Tooltip tooltiptContent={t('threatGroups.tooltips.hover')} side="left" />}
          />
        </ThreatTypesAndGroupsWidgetTitle>
        <DrillDownPie
          data={dataCast(pieChartData.drilldownSeries[0].data)}
          pieColors={PieColors}
          pieSize={isPdf ? 60 : 100}
          options={ChartOptions}
        />
      </ThreatTypesAndGroupsWidgetCard>

      <ThreatTypesAndGroupsWidgetCard>
        <ThreatTypesAndGroupsWidgetTitle>
          <ThreatTypesAndGroupsWidgetTitleText>
            {t('threatGroups.invalidMaliciousActivity.title')}
          </ThreatTypesAndGroupsWidgetTitleText>
          <ThreatGroupToolTip
            onOpenChange={() => {}}
            source="maliciousActivity"
            trigger={<Tooltip tooltiptContent={t('threatGroups.tooltips.hover')} side="left" />}
          />
        </ThreatTypesAndGroupsWidgetTitle>
        <DrillDownPie
          data={dataCast(pieChartData.drilldownSeries[1].data)}
          pieColors={PieColors}
          pieSize={isPdf ? 60 : 100}
          options={ChartOptions}
        />
      </ThreatTypesAndGroupsWidgetCard>
    </ThreatTypesAndGroupsWidgetWrapper>
  )
}
